// src/config/branding.js
// ---------------------------------------------------------------------------
// The guest-facing half of the brand model.
//
// The portal owns the full version in sipnscore-admin/src/config/branding.js:
// presets, warnings, storage shaping, the editor's validation. None of that is
// needed on a phone, so this is deliberately the subset that renders, and it
// must stay behaviour-compatible with the portal's for the four functions it
// shares. If the resolution order changes there, change it here.
//
// Resolution order, same as the portal:
//   the activation's own brand, then its campaign's, then SipNScore's
//
// Everything falls back, so no screen ever has to handle a missing brand.
// ---------------------------------------------------------------------------

export const DEFAULT_BRAND = Object.freeze({
  name: "SipNScore",
  primary: "#c50f2c",
  secondary: "#96661f",
  logoUrl: null,
  bannerUrl: null,
  logoTone: "unknown",
  bannerTone: "unknown",
  logoTransparency: 0,
  bannerTransparency: 0,
  logoPlateMode: "auto",
  bannerPlateMode: "auto",
  plateMode: "auto", // legacy, still read as a fallback for the two above
  bannerFit: "cover",
  bannerScale: 1,
  logoFit: "contain",
  logoScale: 1,
  poweredBy: false,
});

/**
 * Zoom, bounded. Must match the portal's clamp, or a value the portal accepted
 * renders differently on the phone than it did in the preview.
 */
export const SCALE_MIN = 0.5;
export const SCALE_MAX = 2;

export function clampScale(v) {
  // null and "" both coerce to 0 through Number(), which then clamps to the
  // minimum. A document with `bannerScale: null` would have rendered its banner
  // at half size, looking like a layout bug rather than a missing value.
  if (v === null || v === undefined || v === "") return 1;
  const n = Number(v);
  if (!Number.isFinite(n)) return 1;
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, Number(n.toFixed(2))));
}

/** Inline style for a framed brand image, from its fit and zoom. */
export function assetStyle(fit, scale) {
  return {
    objectFit: fit === "contain" ? "contain" : "cover",
    transform: `scale(${clampScale(scale)})`,
  };
}

const HEX_SHORT = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const HEX_FULL = /^#?([0-9a-f]{6})$/i;
const RGB = /^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i;

export function parseColour(input) {
  if (typeof input !== "string") return null;
  const raw = input.trim();
  if (!raw) return null;

  const short = raw.match(HEX_SHORT);
  if (short) {
    const [, r, g, b] = short;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  const full = raw.match(HEX_FULL);
  if (full) return `#${full[1].toLowerCase()}`;
  const rgb = raw.match(RGB);
  if (rgb) {
    const parts = [rgb[1], rgb[2], rgb[3]].map(Number);
    if (parts.some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
    return `#${parts.map((n) => n.toString(16).padStart(2, "0")).join("")}`;
  }
  return null;
}

function toRgb(hex) {
  const clean = parseColour(hex) || DEFAULT_BRAND.primary;
  return {
    r: parseInt(clean.slice(1, 3), 16),
    g: parseInt(clean.slice(3, 5), 16),
    b: parseInt(clean.slice(5, 7), 16),
  };
}

const channel = (v) => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

function luminance(hex) {
  const { r, g, b } = toRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Black or white, whichever is readable on this colour.
 *
 * This is the one that matters most on a phone. A client's brand yellow with
 * white text on it is invisible in a bright bar, and the person who set the
 * colour is not the person holding the phone.
 */
export function readableInkOn(hex) {
  return contrastRatio(hex, "#ffffff") >= contrastRatio(hex, "#111111")
    ? "#ffffff"
    : "#111111";
}

function shade(hex, amount) {
  const { r, g, b } = toRgb(hex);
  const mix = (c) =>
    Math.round(amount < 0 ? c * (1 + amount) : c + (255 - c) * amount)
      .toString(16)
      .padStart(2, "0");
  return `#${mix(r)}${mix(g)}${mix(b)}`;
}

/**
 * Nudge a colour until it is readable on a given background.
 *
 * The whole reason dark mode is more than a background swap. A brand's primary
 * is picked to look right on a client's white packaging, and plenty of them are
 * near-black, which as a heading on a dark phone screen is invisible. Walks the
 * colour away from the background in small steps and stops at the first one
 * that clears the ratio, so the result is the closest readable version of the
 * brand rather than an arbitrary lighter one.
 *
 * Behaviour-compatible with the portal's copy. Change both together.
 */
export function ensureContrast(colour, background, min = 4.5) {
  const start = parseColour(colour) || DEFAULT_BRAND.primary;
  if (contrastRatio(start, background) >= min) return start;

  const towardsLight = luminance(background) < 0.5;
  for (let step = 0.05; step <= 1.0001; step += 0.05) {
    const next = shade(start, towardsLight ? step : -step);
    if (contrastRatio(next, background) >= min) return next;
  }
  return towardsLight ? "#ffffff" : "#111111";
}

/** The two surfaces a brand has to survive. Must match the portal's. */
export const SURFACES = Object.freeze({ light: "#ffffff", dark: "#15181c" });

function alpha(hex, a) {
  const { r, g, b } = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * Only http(s) and data images are ever put in an img src.
 *
 * The value arrives from a Firestore document that the portal writes, but the
 * guest app must not assume the portal is the only thing that ever wrote it.
 */
export function safeAssetUrl(value) {
  const v = typeof value === "string" ? value.trim().slice(0, 2000) : "";
  if (!v) return null;
  if (/^data:image\//i.test(v)) return v;
  if (/^https?:\/\//i.test(v)) return v;
  return null;
}

export function normaliseBrand(raw, inherit = null) {
  const base = inherit ? normaliseBrand(inherit) : DEFAULT_BRAND;
  if (!raw || typeof raw !== "object") return { ...base };

  // Per asset, not per field. The tone, transparency, fit and scale all
  // describe one particular image, so they inherit with it. Merging them
  // separately let an activation's stored defaults override the campaign's real
  // measurements, and the inherited logo lost its backdrop. Must match the
  // portal's copy exactly.
  const ownLogo = safeAssetUrl(raw.logoUrl);
  const logo = ownLogo
    ? {
        logoUrl: ownLogo,
        logoTone: raw.logoTone || "unknown",
        logoTransparency: Number(raw.logoTransparency) || 0,
        logoFit: raw.logoFit || "contain",
        logoScale: clampScale(raw.logoScale),
        // Per image. A wordmark and a photographic banner need different
        // backdrops, and one setting for both meant fixing one broke the other.
        logoPlateMode: raw.logoPlateMode || raw.plateMode || "auto",
      }
    : {
        logoUrl: base.logoUrl,
        logoTone: base.logoTone,
        logoTransparency: base.logoTransparency,
        logoFit: base.logoFit,
        logoScale: base.logoScale,
        logoPlateMode: base.logoPlateMode,
      };

  const ownBanner = safeAssetUrl(raw.bannerUrl);
  const banner = ownBanner
    ? {
        bannerUrl: ownBanner,
        bannerTone: raw.bannerTone || "unknown",
        bannerTransparency: Number(raw.bannerTransparency) || 0,
        bannerFit: raw.bannerFit || "cover",
        bannerScale: clampScale(raw.bannerScale),
        bannerPlateMode: raw.bannerPlateMode || raw.plateMode || "auto",
      }
    : {
        bannerUrl: base.bannerUrl,
        bannerTone: base.bannerTone,
        bannerTransparency: base.bannerTransparency,
        bannerFit: base.bannerFit,
        bannerScale: base.bannerScale,
        bannerPlateMode: base.bannerPlateMode,
      };

  return {
    name:
      (typeof raw.name === "string" && raw.name.trim().slice(0, 80)) || base.name,
    primary: parseColour(raw.primary) || base.primary,
    secondary: parseColour(raw.secondary) || base.secondary,
    ...logo,
    ...banner,
    plateMode: raw.plateMode || base.plateMode || "auto",
    poweredBy:
      typeof raw.poweredBy === "boolean" ? raw.poweredBy : !!logo.logoUrl,
  };
}

/**
 * Resolve the brand a guest should see for one activation.
 *
 * @param {object|null} event  the activation document
 * @param {object|null} group  its campaign document, if it has one
 */
export function resolveBrand(event, group) {
  return normaliseBrand(event?.branding, group?.branding || null);
}

/**
 * Does this artwork need a plate behind it on this surface?
 *
 * A brand mark is nearly always black shapes on a transparent background,
 * because it was drawn for a white page. On a dark phone it is not faint, it is
 * gone. We do not recolour a client's logo: inverting a two-colour mark ruins
 * it. It gets the background it was drawn for instead, only where needed.
 *
 * Must match the portal's copy in sipnscore-admin/src/config/branding.js.
 */
const PLATE_LIGHT = "#f4f3f1";
const PLATE_DARK = "#1c2026";

export function needsPlate(tone, scheme, mode = "auto") {
  if (mode === "light" || mode === "dark" || mode === "always") return true;
  if (mode === "none" || mode === "never") return false;
  if (scheme === "dark") return tone === "dark" || tone === "mixed";
  // A white-on-transparent logo on a white page is the same problem in reverse.
  return tone === "light";
}

/** Below this, artwork is effectively solid and covers whatever is behind it. */
const MOSTLY_OPAQUE = 0.15;

/**
 * What sits behind this artwork on this surface.
 *
 * Decided from the measured artwork rather than fixed in CSS, because every
 * campaign brings different artwork. Returned as a value and applied inline, so
 * no stylesheet rule can override it by sitting later in the file.
 *
 * Must match the portal's copy exactly, or the preview stops predicting the
 * phone, which is the one thing it is for.
 */
export function brandFrame({ tone, transparency = 0, scheme, mode = "auto" }) {
  const transparent = transparency >= MOSTLY_OPAQUE;

  const forced = mode === "light" || mode === "dark" || mode === "always";
  if (!transparent && !forced) {
    return { background: "transparent", plated: false, fit: null };
  }

  if (needsPlate(tone, scheme, mode)) {
    const background =
      mode === "light" || mode === "always"
        ? PLATE_LIGHT
        : mode === "dark"
        ? PLATE_DARK
        : scheme === "dark"
        ? PLATE_LIGHT
        : PLATE_DARK;
    return { background, plated: true, fit: "contain" };
  }

  // Readable as it is. No box: a slab behind a logo that does not need one is
  // just a rectangle on the screen.
  return { background: "transparent", plated: false, fit: "contain" };
}

/** CSS custom properties, spread onto a wrapper element. */
export function brandVars(brand) {
  const b = normaliseBrand(brand);
  return {
    "--brand-primary": b.primary,
    "--brand-primary-ink": readableInkOn(b.primary),
    "--brand-primary-dark": shade(b.primary, -0.25),
    "--brand-primary-soft": alpha(b.primary, 0.1),
    "--brand-secondary": b.secondary,
    "--brand-secondary-ink": readableInkOn(b.secondary),

    // The brand as TEXT, one variant per surface. CSS cannot do contrast maths,
    // so both are computed here and App.css picks with a media query. Without
    // this, a dark brand heading disappears the moment a guest's phone is in
    // dark mode, which is most phones, most evenings, which is when bars are
    // busy.
    "--brand-on-light": ensureContrast(b.primary, SURFACES.light),
    "--brand-on-dark": ensureContrast(b.primary, SURFACES.dark),
  };
}
